import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StarFilledIcon from "@mui/icons-material/StarOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutlineOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ReactNode, useRef } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { createQuoteComparisonView } from "../../api/dossier/createQuoteComparisonView";
import { getDossier } from "../../api/dossier/getDossier";
import { getDossierStatus } from "../../api/dossier/getDossierStatus";
import { getQuotesWithVehicleSpecifications } from "../../api/dossier/getQuotesWithVehicleSpecifications";
import { patchQuoteComparisonView } from "../../api/dossier/patchQuoteComparisonView";
import { setPrimaryQuote } from "../../api/dossier/setPrimaryQuote";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import HomeButton from "../../components/buttons/HomeButton";
import APComparison, {
  Comparison,
  ComparisonCriterion,
} from "../../components/comparison/APComparison";
import APComparisonSummary from "../../components/comparison/APComparisonSummary";
import PageContainer from "../../components/PageContainer";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import {
  getSummarySortFn,
  parseComparisonParams,
} from "../../helpers/comparison";
import { isAPQuoteHideInvalidCalculationsEnabled } from "../../helpers/configuration";
import { toDollars, toFormattedNumber } from "../../helpers/currencies";
import { filterQuotes } from "../../helpers/quotes";
import {
  standardValueFormatter,
  standardVehicleValueFormatter,
} from "../../helpers/vehicle";
import { useComparisonViewTracking } from "../../hooks/useComparisonViewTracking";
import { useFaqs } from "../../hooks/useFaqs";
import { useAuth } from "../../providers/AuthProvider";
import { buildNextActionRoute, NextDriverAction } from "../../types/Dossier";
import {
  CriteriaLabelMap,
  QuoteComparisonItem,
  QuoteWithVehicleSpecification,
} from "../../types/Quote";
import QuoteComparisonName from "./components/QuoteComparisonName";

const defaultBottomCriteria: (keyof QuoteComparisonItem)[] = [
  "weeklyNetCost",
  "savingsPercentage",
  "annualSavings",
  "primary",
];

const QuoteComparison = () => {
  const [searchParams] = useSearchParams();
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const { getScopedPageUrl, inAppScope } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const quoteIds = searchParams.get("quoteIds")?.split(",") ?? [];
  const selectedCriteria =
    searchParams.get("selectedCriteria")?.split(",") ?? [];
  const initialPrimaryCriteria =
    searchParams.get("primaryCriteria") ?? undefined;
  const quoteNameFields: Record<string, ReactNode> = {};

  const quotesQuery = getQuotesWithVehicleSpecifications(
    dossierId ?? "",
    quoteIds
  );
  const {
    mutateAsync: mutatePrimaryToggleAsync,
    isLoading: isLoadingPrimaryToggle,
  } = setPrimaryQuote(dossierId || "", {
    mutationOptions: {
      successAlertMessage: "Your primary quote has been updated",
    },
  });

  // View tracking
  const { mutateAsync: mutateCreateQuoteComparisonViewAsync } =
    createQuoteComparisonView(dossierId || "");
  const { mutateAsync: mutatePatchQuoteComparisonViewAsync } =
    patchQuoteComparisonView(dossierId || "");

  const _createView = async () => {
    const [quote1Id, quote2Id, quote3Id, quote4Id, quote5Id] = quoteIds;
    const _viewId = await mutateCreateQuoteComparisonViewAsync({
      primaryCriteria: initialPrimaryCriteria || "",
      selectedCriteria: selectedCriteria.join(","),
      quote1Id,
      quote2Id,
      quote3Id,
      quote4Id,
      quote5Id,
    });

    return _viewId;
  };

  const _patchView = async (viewId: string, sortHistory: string[]) => {
    await mutatePatchQuoteComparisonViewAsync({
      quoteComparisonViewId: viewId,
      clickedCriteria: sortHistory.join(","),
    });
  };

  const { onSort } = useComparisonViewTracking({
    createView: inAppScope("external")
      ? _createView
      : () => Promise.resolve(""),
    patchView: inAppScope("external") ? _patchView : () => Promise.resolve(),
  });

  const dossier = getDossier(dossierId ?? "");
  const dossierStatus = getDossierStatus(dossierId ?? "");
  const configMap = getPublicConfig();

  if (quotesQuery.isError) {
    throw quotesQuery.error;
  }

  if (dossier.isError) {
    throw dossier.error;
  }

  if (dossierStatus.isError) {
    throw dossierStatus.error;
  }

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "quote-compare-view",
    contextIds: {
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  const onNext = async (nextAction: NextDriverAction) => {
    if (dossierId) {
      const route = buildNextActionRoute(dossierId, nextAction);
      navigate(route);
    }
  };

  const renderQuoteId = (item: QuoteComparisonItem) => {
    if (!quoteNameFields[`${item.quoteId}-${item.name}`]) {
      quoteNameFields[`${item.quoteId}-${item.name}`] = (
        <QuoteComparisonName
          key={`${item.quoteId}-${item.name}`}
          id={item.quoteId}
          dossierId={dossierId ?? ""}
          quoteComparisonIds={quoteIds}
          name={item.name}
          isLocked={item.isLocked}
        />
      );
    }

    return (
      <Box sx={{ width: "100%" }}>
        <Link to={getScopedPageUrl("viewQuote", item.quoteId)}>
          <Box sx={{ display: "flex", columnGap: 1, height: "40px" }}>
            <Box
              width="40px"
              height="40px"
              component="img"
              src={item.imageUrl}
              alt={item.name}
              sx={{ objectFit: "cover" }}
            />
            <Box>
              <Typography variant="bodySmall">
                {item.make} {item.model}
              </Typography>
            </Box>
          </Box>
        </Link>
        <Box mt={1}>{quoteNameFields[`${item.quoteId}-${item.name}`]}</Box>
      </Box>
    );
  };

  const renderPrimary = (item: QuoteComparisonItem) => {
    return item.primary ? (
      <StarFilledIcon color="secondary" />
    ) : (
      <IconButton
        size="large"
        aria-label="favourite-quote"
        color="onSurface"
        onClick={() => mutatePrimaryToggleAsync({ quoteId: item.quoteId })}
        disabled={isLoadingPrimaryToggle || dossier?.data?.hasLockedQuote}
        sx={{
          p: 0,
          display: dossier?.data?.hasLockedQuote ? "none" : undefined,
        }}
      >
        <StarOutlineIcon />
      </IconButton>
    );
  };

  const buildComparison = (
    quotes: QuoteWithVehicleSpecification[]
  ): Comparison<QuoteComparisonItem> => {
    const items: QuoteComparisonItem[] = quotes.map((quote) => ({
      quoteId: quote.identifiers.crmQuoteId,
      isLocked: quote.config.isLocked,
      name: quote.identifiers.name,
      make: quote.vehicle.make,
      model: quote.vehicle.model,
      year: quote.vehicle.year,
      imageUrl: quote.vehicle.primaryImageUrl ?? "/photo-placeholder.png",
      savingsPercentage: Math.round(quote.salaryComparison.savingsPercentage),
      weeklyNetCost: Math.round(quote.salaryComparison.netCost.weekly),
      summary: quote.identifiers.crmQuoteId,
      annualSavings: Math.round(quote.salaryComparison.savings.annual),
      "quote.annualKm": quote.vehicle.budgetedAnnualKm,
      "quote.term": quote.term.termMonths,
      "quote.businessUsage": quote.vehicle.businessUsePercentage,
      "quote.residual": quote.vehicle.residual.incGst,
      "vehicle.name": `${quote.vehicle.year} ${quote.vehicle.make} ${
        quote.vehicle.model
      } ${quote.vehicle.variantName ?? ""}`,
      "vehicle.onRoadCost": Math.round(quote.vehicle.onroadCosts),
      "vehicle.warrantYrs": quote.vehicle.specification?.warrantyYears ?? null,
      "vehicle.powerPlant": quote.vehicle.specification?.powerPlantType
        ? standardVehicleValueFormatter(
            "powerPlantType",
            quote.vehicle.specification.powerPlantType,
            {
              fallbacks: quote.vehicle.specification.unMappedValues,
            }
          )
        : null,
      "vehicle.powerKw": quote.vehicle.specification?.powerKw ?? null,
      "vehicle.torque": quote.vehicle.specification?.torque ?? null,
      "vehicle.seats": quote.vehicle.specification?.seatCapacity ?? null,
      "vehicle.acceleration": quote.vehicle.specification?.acceleration ?? null,
      "vehicle.cargoVolume":
        quote.vehicle.specification?.minCargoVolume ?? null,
      "vehicle.fuelEconomy": quote.vehicle.specification?.fuelEconomy ?? null,
      "vehicle.range": quote.vehicle.specification?.range ?? null,
      "vehicle.ancapRating": quote.vehicle.specification?.ancaprating
        ? parseInt(quote.vehicle.specification.ancaprating)
        : null,
      primary:
        dossier?.data?.opportunity?.primaryQuoteId ===
        quote.identifiers.crmQuoteId,
    }));

    const {
      parsedInitialPrimaryCriteria,
      parsedSelectedCriteria,
      summaryCriteria,
    } = parseComparisonParams(
      items[0],
      initialPrimaryCriteria,
      "weeklyNetCost",
      selectedCriteria,
      "summary"
    );

    const criteria: Partial<
      Record<
        keyof QuoteComparisonItem,
        ComparisonCriterion<QuoteComparisonItem>
      >
    > = {
      quoteId: {
        label: "",
        valueRenderer: (item) => renderQuoteId(item),
      },
      savingsPercentage: {
        label: CriteriaLabelMap.savingsPercentage,
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => `${value}%`,
        getAppLocationId: () => "QuoteCompareView-SavingsPct",
      },
      weeklyNetCost: {
        label: CriteriaLabelMap.weeklyNetCost,
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) => toDollars(value as number, 0),
        getAppLocationId: () => "QuoteCompareView-WeeklyNetCost",
      },
      annualSavings: {
        label: CriteriaLabelMap.annualSavings,
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => toDollars(value as number, 0),
        getAppLocationId: () => "QuoteCompareView-AnnualSavings",
      },
      "quote.annualKm": {
        label: CriteriaLabelMap["quote.annualKm"],
        sortable: {
          direction: "asc",
        },
        getAppLocationId: () => "QuoteCompareView-AnnualKm",
        valueFormatter: (value) =>
          `${toFormattedNumber(value as number, 0)} km`,
      },
      "quote.term": {
        label: CriteriaLabelMap["quote.term"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => `${value} mths`,
        getAppLocationId: () => "QuoteCompareView-Term",
      },
      "quote.businessUsage": {
        label: CriteriaLabelMap["quote.businessUsage"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => `${value}%`,
        getAppLocationId: () => "QuoteCompareView-BusinessUsagePct",
      },
      "quote.residual": {
        label: CriteriaLabelMap["quote.residual"],
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) => toDollars(value as number, 0),
        getAppLocationId: () => "QuoteCompareView-Residual",
      },
      "vehicle.name": {
        label: CriteriaLabelMap["vehicle.name"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        styles: {
          topAlign: true,
        },
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },
      "vehicle.onRoadCost": {
        label: CriteriaLabelMap["vehicle.onRoadCost"],
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) => toDollars(value as number, 0),
        getAppLocationId: () => "QuoteCompareView-Onroad",
      },
      "vehicle.powerPlant": {
        label: CriteriaLabelMap["vehicle.powerPlant"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        valueFormatter: (value) => standardValueFormatter(value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },
      "vehicle.warrantYrs": {
        label: CriteriaLabelMap["vehicle.warrantYrs"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("warrantyYears", value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },
      "vehicle.powerKw": {
        label: CriteriaLabelMap["vehicle.powerKw"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("powerKw", value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },
      "vehicle.torque": {
        label: CriteriaLabelMap["vehicle.torque"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("torque", value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },
      "vehicle.seats": {
        label: CriteriaLabelMap["vehicle.seats"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("seatCapacity", value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },
      "vehicle.acceleration": {
        label: CriteriaLabelMap["vehicle.acceleration"],
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("acceleration", value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },
      "vehicle.cargoVolume": {
        label: CriteriaLabelMap["vehicle.cargoVolume"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("minCargoVolume", value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },

      "vehicle.fuelEconomy": {
        label: CriteriaLabelMap["vehicle.fuelEconomy"],
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("fuelEconomy", value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },

      "vehicle.range": {
        label: CriteriaLabelMap["vehicle.range"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("range", value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },

      "vehicle.ancapRating": {
        label: CriteriaLabelMap["vehicle.ancapRating"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("ancaprating", value),
        getAppLocationId: () => "QuoteCompareView-VehicleInfo",
      },
      primary: {
        label: CriteriaLabelMap.primary,
        sortable: {
          direction: "desc",
        },
        valueRenderer: (item) => renderPrimary(item),
      },
    };

    return {
      items,
      criteria: {
        ...criteria,
        summary: {
          label: CriteriaLabelMap.summary,
          sortable: {
            direction: "desc",
            function: getSummarySortFn(items, criteria, summaryCriteria),
          },
          styles: {
            topAlign: true,
          },
          valueRenderer: (item) => (
            <APComparisonSummary
              selectedItem={item}
              items={items}
              criteria={criteria}
              summaryCriteria={summaryCriteria}
              criteriaLabelMap={CriteriaLabelMap}
            />
          ),
        },
      },
      headerCriteria: "quoteId",
      initialPrimaryCriteria: parsedInitialPrimaryCriteria,
      selectedCriteria: parsedSelectedCriteria,
      alwaysShownBottomCriteria: defaultBottomCriteria,
      summaryCriteria: "summary",
      expandableCriteria: [
        {
          id: "quote",
          label: "Quote",
          criteria: [
            "quote.annualKm",
            "quote.term",
            "quote.businessUsage",
            "quote.residual",
          ],
          expandByDefault: false,
        },
        {
          id: "vehicle",
          label: "Vehicle",
          criteria: [
            "vehicle.name",
            "vehicle.onRoadCost",
            "vehicle.powerPlant",
            "vehicle.warrantYrs",
            "vehicle.powerKw",
            "vehicle.torque",
            "vehicle.seats",
            "vehicle.acceleration",
            "vehicle.cargoVolume",
            "vehicle.fuelEconomy",
            "vehicle.range",
            "vehicle.ancapRating",
          ],
          expandByDefault: false,
        },
      ],
      highlightWinner: true,
      faqProps,
      onSort,
    };
  };

  const isLoading =
    quotesQuery.isLoading ||
    dossier.isLoading ||
    dossierStatus.isLoading ||
    configMap.isLoading ||
    isLoadingFaqs;
  const isSuccess =
    quotesQuery.isSuccess &&
    dossier.isSuccess &&
    dossierStatus.isSuccess &&
    configMap.isSuccess &&
    isSuccessFaqs;

  const isInvalidCalculationsHidden =
    configMap.isSuccess &&
    isAPQuoteHideInvalidCalculationsEnabled(configMap.data);
  const filteredQuotes = quotesQuery.isSuccess
    ? filterQuotes(
        quotesQuery.data,
        inAppScope("internal"),
        isInvalidCalculationsHidden
      )
    : [];
  const comparison = isSuccess ? buildComparison(filteredQuotes) : null;

  return (
    <PageContainer
      title="Compare Quotes"
      loading={isLoading}
      containerRef={containerRef}
    >
      {isSuccess && comparison && (
        <>
          <APComparison comparison={comparison} containerRef={containerRef} />
          <Box
            sx={{
              pt: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Stack
              spacing={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Box display="flex" gap={1}>
                <BackButton /> <HomeButton destination="dossiersHome" />
              </Box>
              <RestrictedAppScope scope="external">
                <Box>
                  <APContactHelpButton
                    regardingOpportunityId={
                      dossier.data.opportunity?.id ?? undefined
                    }
                  />
                </Box>
              </RestrictedAppScope>
            </Stack>
            <Stack
              spacing={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Button
                size="large"
                color="secondary"
                variant="contained"
                disableElevation={true}
                startIcon={<PlayArrowOutlinedIcon />}
                onClick={() => onNext(dossierStatus.data.nextDriverAction)}
              >
                Next
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </PageContainer>
  );
};

export default QuoteComparison;
